import React from "react";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import { Divider, Menu, MenuItem, Paper, Typography } from "@mui/material";
import { Language } from "../../shared/interfaces/GameLogicTypes";
import StarIcon from "@mui/icons-material/Star";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { getFontSizeByTitle } from "../../shared/helper/functions";
import { t } from "i18next";
import { Trans } from "react-i18next";

interface CardDeckProps {
  title: string;
  questions?: number;
  answers?: number;
  categories?: number;
  playCount?: number;
  language?: Language;
  creatorName?: string;
  id: string;
  selected?: boolean;
  onlyTitle?: boolean;
  featured?: boolean;
  compact?: boolean;
  hideLanguage?: boolean;
  emoji?: string;
  // New explicit icon props (lobby/browser)
  onPreview?: () => void;
  onSelectToggle?: () => void;
  // Legacy three-dots menu (profile page)
  options?: Array<{ name: string; function: () => void }>;
}

export default function CardDeck(props: CardDeckProps) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const height = props.compact
    ? "6em"
    : props.onlyTitle
    ? "5em"
    : props.creatorName
    ? "16em"
    : "12em";

  return (
    <Paper
      onClick={props.onPreview ? () => props.onPreview!() : props.onSelectToggle}
      sx={{
        position: "relative",
        border: "solid",
        borderWidth: props.selected ? "3px" : "0px",
        borderColor: "primary.main",
        transition: "border-width .1s ease-in-out",
        zIndex: 0,
        flex: 1,
        cursor: (props.onPreview || props.onSelectToggle) ? "pointer" : "default",
        backgroundColor: "#f5f3f0",
      }}
    >
      {/* Top-center: play count badge */}
      {props.playCount !== undefined && props.playCount > 0 && !props.compact && !props.onlyTitle && (
        <Typography
          variant="caption"
          sx={{
            position: "absolute",
            top: -11,
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "rgba(186, 186, 186, 0.82)",
            borderRadius: "999px",
            px: 1,
            py: 0.1,
            whiteSpace: "nowrap",
            lineHeight: 1.6,
            zIndex: 1,
          }}
        >
          {props.playCount} {props.playCount === 1 ? t("card_deck_specs_play") : t("card_deck_specs_plays")}
        </Typography>
      )}

      {/* Top-left: featured star indicator */}
      {props.featured && (
        <StarIcon
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            color: "primary.main",
            fontSize: props.compact ? 14 : 18,
            pointerEvents: "none",
            zIndex: 0,
          }}
        />
      )}

      {/* Top-right: checkmark toggle OR three-dots menu */}
      {props.onSelectToggle ? (
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); props.onSelectToggle!(); }} sx={{ position: "absolute", top: 0, right: 0, p: 0.5 }}>
          {props.selected ? (
            <CheckCircleIcon fontSize="small" color="primary" />
          ) : (
            <RadioButtonUncheckedIcon fontSize="small" />
          )}
        </IconButton>
      ) : props.options ? (
        <>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); setAnchorEl(e.currentTarget); }}
            sx={{ position: "absolute", top: 0, right: 0 }}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
            {props.options.map((option) => (
              <MenuItem key={option.name} onClick={() => { option.function(); setAnchorEl(null); }}>
                {option.name}
              </MenuItem>
            ))}
          </Menu>
        </>
      ) : null}

      <Stack
        sx={{ margin: ".5em", height }}
        justifyContent="space-between"
      >
        <Stack
          sx={{ height: props.onlyTitle || props.compact ? "100%" : "45%", paddingInline: props.compact ? "0.4em" : "1em" }}
          justifyContent="center"
          alignItems="center"
        >
          <Typography
            component={props.compact ? "span" : "pre"}
            sx={{
              marginBottom: "0em",
              fontSize: props.compact
                ? (props.title.length > 20 ? "0.5rem" : props.title.length > 12 ? "0.6rem" : "0.7rem")
                : getFontSizeByTitle(props.title, props.onlyTitle),
              fontWeight: "bold",
              ...(props.compact
                ? { whiteSpace: "normal", overflow: "hidden", maxHeight: "3.6em", lineHeight: 1.2 }
                : { whiteSpace: "pre-wrap", wordWrap: "break-word", overflowWrap: "break-word" }),
              maxWidth: "100%",
            }}
            align="center"
            paragraph
            variant="body1"
          >
            {props.title}
          </Typography>
        </Stack>

        {!props.compact && !props.onlyTitle && props.emoji && props.questions === undefined && (
            <>
              <Divider />
              <Stack justifyContent="center" alignItems="center" sx={{ height: "50%", marginBottom: ".2em" }}>
                <Typography sx={{ fontSize: "2.86rem", lineHeight: 1 }}>{props.emoji}</Typography>
              </Stack>
            </>
          )}

        {!props.compact &&
          !props.onlyTitle &&
          props.language !== undefined &&
          props.questions !== undefined &&
          props.answers !== undefined &&
          props.categories !== undefined && (
            <>
              <Divider />
              <Stack justifyContent="space-between" sx={{ height: "50%", marginBottom: ".2em" }} spacing={0}>
                {!props.hideLanguage && (
                  <Typography sx={{ margin: 0 }} align="center" paragraph variant="body1">
                    <Trans values={{ language: props.language.name }}>{t("card_deck_specs_language")}</Trans>
                  </Typography>
                )}
                <Typography sx={{ margin: 0 }} align="center" paragraph variant="body1">
                  <Trans values={{ questions: props.questions }}>{t("card_deck_specs_questions")}</Trans>
                </Typography>
                <Typography sx={{ margin: 0 }} align="center" paragraph variant="body1">
                  <Trans values={{ answers: props.answers }}>{t("card_deck_specs_answers")}</Trans>
                </Typography>
                <Typography sx={{ margin: 0 }} align="center" paragraph variant="body1">
                  <Trans values={{ categories: props.categories }}>{t("card_deck_specs_categories")}</Trans>
                </Typography>
                {props.creatorName && (
                  <>
                    <Divider />
                    <Typography sx={{ margin: 0, color: "primary.main" }} align="center" paragraph variant="body1">
                      {props.creatorName}
                    </Typography>
                  </>
                )}
              </Stack>
            </>
          )}
      </Stack>
    </Paper>
  );
}
